import { EntityCommonInfoModel, FilteredPagedQueryResult, FilteredPagedModel, CommonMultiDeleteModel } from "../../entities/common";
import { apiSlice, TagTypes } from "./apiSlice";
import { getQuerySearchParams } from "../../utils";
import { OaCostModel } from "../../entities/oaCost";

export const oaCostApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOaCostCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => `/oacost/commonInfo`,
      providesTags: [{ type: TagTypes.OaCost, id: "LIST" }],
    }),
    getOaCostFilteredPaged: builder.query<FilteredPagedQueryResult<OaCostModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/oacost/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.OaCost, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.OaCost,
                id,
              })),
            ]
          : [{ type: TagTypes.OaCost, id: "PARTIAL-LIST" }],
    }),
    getOaCost: builder.query<OaCostModel, number>({
      query: (oaCostId) => `/oacost/getById/${oaCostId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.OaCost, id: arg }],
    }),
    scrapOaCost: builder.query<OaCostModel, { tracingNumber: string }>({
      query: ({ tracingNumber }) => ({
        url: "/oacost/scrap",
        method: "POST",
        body: { tracingNumber },
      }),
      providesTags: [{ type: TagTypes.OaCost, id: "Scrap" }],
    }),
    addOaCost: builder.mutation({
      query: (initialCost) => ({
        url: "/oacost",
        method: "POST",
        body: initialCost,
      }),
      invalidatesTags: [
        { type: TagTypes.OaCost, id: "LIST" },
        { type: TagTypes.OaCost, id: "PARTIAL-LIST" },
      ],
    }),
    editOaCost: builder.mutation({
      query: (oaCost) => ({
        url: `/oacost/${oaCost.id}`,
        method: "PUT",
        body: oaCost,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.OaCost, id: arg.id }],
    }),
    deleteOaCost: builder.mutation({
      query: (oaCostId) => ({
        url: `/oacost/${oaCostId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.OaCost, id: arg },
              { type: TagTypes.OaCost, id: "LIST" },
            ],
    }),
    deleteMultiOaCosts: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/oacost/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.OaCost,
                id,
              })),
              { type: TagTypes.OaCost, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetOaCostCommonInfoQuery,
  useGetOaCostFilteredPagedQuery,
  useGetOaCostQuery,
  useAddOaCostMutation,
  useEditOaCostMutation,
  useScrapOaCostQuery,
  useLazyScrapOaCostQuery,
  useDeleteOaCostMutation,
  useDeleteMultiOaCostsMutation,
} = oaCostApi;
